import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import {
  ArrowLeft,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Code2,
  Columns3,
  Eye,
  GripVertical,
  LayoutTemplate,
  List as ListIcon,
  Package,
  Pencil,
  Plus,
  Receipt,
  Save,
  ShieldCheck,
  Table2,
  Trash2,
  Type,
  Users,
  Wallet,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import emailLogo from '@/assets/email-logo.webp';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SelectField,
  Textarea,
  useToast,
} from '@/components/ui';
import useAutoResizeTextarea from '@/hooks/useAutoResizeTextarea';
import {
  useCreateEmailTemplate,
  useEmailTemplate,
  useEmailVariables,
  useUpdateEmailTemplate,
} from '@/hooks/useEmailTemplates';
import {
  BLOCK_TYPE_LABELS,
  createBlock,
  type EmailBlock,
  type EmailBlockType,
  htmlToPlainText,
  parseBlocksFromHtml,
  renderBlocksToHtml,
  TC_PROMPT_DEFAULT,
} from '@/lib/emailBlocks';
import { ApiError, EmailTemplateCategory, EmailVariableDefinition } from '@/types';
import cn from '@/utils/cn';
import { EMAIL_TEMPLATE_CATEGORY_OPTIONS, ROUTES } from '@/utils/constants';
import { stripExecutable } from '@/utils/stripExecutable';

// 'split' shows variables + editor + preview side by side; 'editor' and
// 'preview' each expand their pane to the full page width.
type ViewMode = 'split' | 'editor' | 'preview';

const VIEW_MODE_OPTIONS: { value: ViewMode; label: string; icon: typeof Columns3 }[] = [
  { value: 'split', label: 'Split view', icon: Columns3 },
  { value: 'editor', label: 'Edit', icon: Pencil },
  { value: 'preview', label: 'Preview', icon: Eye },
];

const BLOCK_TYPE_ICONS: Record<EmailBlockType, typeof Type> = {
  heading: LayoutTemplate,
  paragraph: Type,
  list: ListIcon,
  table: Table2,
  bankDetails: Wallet,
  productsTable: Package,
  billingTotals: Receipt,
  tcLinks: ShieldCheck,
  additionalArns: Users,
  installments: CalendarClock,
  raw: Code2,
};

const ADD_BLOCK_TYPES: EmailBlockType[] = [
  'heading',
  'paragraph',
  'list',
  'table',
  'bankDetails',
  'productsTable',
  'billingTotals',
  'tcLinks',
  'additionalArns',
  'installments',
  'raw',
];

const slugify = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const optionalField = z.string().trim().max(500).optional();

// Derived from EMAIL_TEMPLATE_CATEGORY_OPTIONS (utils/constants.ts) rather than
// a separate literal list, so this form can't drift out of sync with the
// category dropdown's own options.
const categoryValues = EMAIL_TEMPLATE_CATEGORY_OPTIONS.map((option) => option.value) as [
  EmailTemplateCategory,
  ...EmailTemplateCategory[],
];

const REFERENCE_TYPE_CODES = ['QT', 'RN', 'INV', 'SV', 'PC'] as const;

const metaSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(150),
  slug: z.string().trim().min(1, 'Slug is required').max(100),
  category: z.enum(categoryValues),
  department: z.string().trim().max(50).optional(),
  referenceTypeCode: z.enum(REFERENCE_TYPE_CODES).or(z.literal('')).optional(),
  subject: z.string().trim().min(1, 'Subject is required').max(500),
  defaultTo: optionalField,
  defaultCc: optionalField,
  defaultBcc: optionalField,
  replyTo: z.string().trim().max(150).optional(),
  isActive: z.boolean(),
});

type MetaFormValues = z.infer<typeof metaSchema>;

// Pure array helpers for the list/table block editors below - keeping these
// as named top-level functions (instead of inlining the .map/.filter calls
// inside each input's onChange/onFocus) keeps the JSX callbacks to a single
// call instead of a function nested several levels deep.
function replaceAt<T>(arr: T[], index: number, value: T): T[] {
  return arr.map((item, i) => (i === index ? value : item));
}
function removeAt<T>(arr: T[], index: number): T[] {
  return arr.filter((_, i) => i !== index);
}
const replaceCellAt = (
  rows: string[][],
  rowIndex: number,
  colIndex: number,
  value: string,
): string[][] => rows.map((row, ri) => (ri === rowIndex ? replaceAt(row, colIndex, value) : row));
const removeColumnAt = (rows: string[][], colIndex: number): string[][] =>
  rows.map((row) => removeAt(row, colIndex));

const substitute = (html: string, variables: EmailVariableDefinition[]): string =>
  variables.reduce((acc, variable) => acc.split(`{{${variable.key}}}`).join(variable.sample), html);

const buildPreviewDoc = (bodyHtml: string, variables: EmailVariableDefinition[]): string => `
<!doctype html>
<html>
<head>
<meta charset="utf-8">
<style>
  html, body { font-family: 'Plus Jakarta Sans', Arial, sans-serif; margin: 0; background: #F6F1E5; }
  table { width: 100%; }
</style>
</head>
<body>
  <div id="ak-preview-root" style="max-width: 800px; margin: 0 auto; background: #FFFFFF;">
    <div style="padding: 18px 24px; border-bottom: 4px solid #067FFA;">
      <img src="${emailLogo}" alt="AdvisorKhoj" style="height: 28px; display: block;">
    </div>
    <div style="padding: 24px; color: #1A1A18; font-size: 14px; line-height: 1.5;">
      ${stripExecutable(substitute(bodyHtml, variables))}
    </div>
    <div style="padding: 14px 24px; background: #F6F1E5; font-size: 11px; color: #1A1A18; opacity: 0.6;">
      This is a preview using sample placeholder values, not real data.
    </div>
  </div>
</body>
</html>`;

const fieldClass =
  'w-full rounded-md border border-line bg-white px-2.5 py-1.5 text-xs text-ink shadow-sm ' +
  'placeholder:text-ink/40 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20';

interface ActiveField {
  element: HTMLInputElement | HTMLTextAreaElement;
  getValue: () => string;
  setValue: (next: string) => void;
}

interface BlockCardProps {
  block: EmailBlock;
  dragging: boolean;
  onDragStart: () => void;
  onDragOver: () => void;
  onDragEnd: () => void;
  onChange: (block: EmailBlock) => void;
  onRemove: () => void;
  onFieldFocus: (field: ActiveField) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

const BlockCard = ({
  block,
  dragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  onChange,
  onRemove,
  onFieldFocus,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: BlockCardProps) => {
  const Icon = BLOCK_TYPE_ICONS[block.type];
  const paragraphResize = useAutoResizeTextarea(block.type === 'paragraph' ? block.text : '');
  const rawResize = useAutoResizeTextarea(block.type === 'raw' ? block.html : '');

  return (
    <div
      role="group"
      aria-label={`${BLOCK_TYPE_LABELS[block.type]} block`}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      className={cn(
        'rounded-lg border border-line bg-cream/30 p-3 transition-opacity',
        dragging && 'opacity-40',
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-ink/50">
          <button
            type="button"
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            aria-label="Drag to reorder"
            className="cursor-grab bg-transparent p-0 active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <div className="flex flex-col">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              aria-label="Move block up"
              className="text-ink/40 transition-colors hover:text-brand disabled:opacity-20"
            >
              <ChevronUp className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              aria-label="Move block down"
              className="text-ink/40 transition-colors hover:text-brand disabled:opacity-20"
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>
          <Icon className="h-3.5 w-3.5" />
          <span className="text-[11px] font-bold uppercase tracking-wide">
            {BLOCK_TYPE_LABELS[block.type]}
          </span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove block"
          className="text-ink/40 transition-colors hover:text-danger"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {block.type === 'heading' && (
        <div className="flex flex-col gap-1.5">
          <input
            value={block.title}
            onChange={(e) => onChange({ ...block, title: e.target.value })}
            onFocus={(e) =>
              onFieldFocus({
                element: e.target,
                getValue: () => block.title,
                setValue: (next) => onChange({ ...block, title: next }),
              })
            }
            placeholder="Section title"
            className={fieldClass}
          />
          <input
            value={block.meta}
            onChange={(e) => onChange({ ...block, meta: e.target.value })}
            onFocus={(e) =>
              onFieldFocus({
                element: e.target,
                getValue: () => block.meta,
                setValue: (next) => onChange({ ...block, meta: next }),
              })
            }
            placeholder="ARN: {{arn}} | {{company_name}}"
            className={cn(fieldClass, 'font-mono')}
          />
        </div>
      )}

      {block.type === 'paragraph' && (
        <div className="flex flex-col gap-1.5">
          <textarea
            ref={paragraphResize.ref}
            value={block.text}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            onFocus={(e) =>
              onFieldFocus({
                element: e.target,
                getValue: () => block.text,
                setValue: (next) => onChange({ ...block, text: next }),
              })
            }
            rows={2}
            placeholder="Dear {{client_name}}, ..."
            style={{ overflowAnchor: 'none', minHeight: '2.75rem' }}
            className={cn(fieldClass, 'resize-none overflow-hidden')}
          />
          <div className="flex gap-4">
            <Checkbox
              label="Bold"
              checked={block.bold}
              onChange={(e) => onChange({ ...block, bold: e.target.checked })}
            />
            <Checkbox
              label="Italic"
              checked={block.italic ?? false}
              onChange={(e) => onChange({ ...block, italic: e.target.checked })}
            />
          </div>
        </div>
      )}

      {block.type === 'list' && (
        <div className="flex flex-col gap-1.5">
          {block.items.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="flex items-center gap-1.5">
              <input
                value={item}
                onChange={(e) =>
                  onChange({
                    ...block,
                    items: replaceAt(block.items, i, e.target.value),
                  })
                }
                onFocus={(e) =>
                  onFieldFocus({
                    element: e.target,
                    getValue: () => item,
                    setValue: (next) =>
                      onChange({
                        ...block,
                        items: replaceAt(block.items, i, next),
                      }),
                  })
                }
                placeholder={`Item ${i + 1}`}
                className={fieldClass}
              />
              <button
                type="button"
                onClick={() => onChange({ ...block, items: removeAt(block.items, i) })}
                disabled={block.items.length <= 1}
                aria-label="Remove item"
                className="text-ink/40 transition-colors hover:text-danger disabled:opacity-30"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => onChange({ ...block, items: [...block.items, ''] })}
            className="flex w-fit items-center gap-1 text-xs font-semibold text-brand hover:text-brand-hover"
          >
            <Plus className="h-3.5 w-3.5" />
            Add item
          </button>
        </div>
      )}

      {block.type === 'table' && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-start gap-1.5">
            {block.headers.map((header, c) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={c} className="flex min-w-0 flex-1 flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...block,
                      headers: removeAt(block.headers, c),
                      rows: removeColumnAt(block.rows, c),
                    })
                  }
                  disabled={block.headers.length <= 1}
                  aria-label={`Delete column ${c + 1}`}
                  className="flex items-center justify-center gap-0.5 py-0.5 text-[10px] text-ink/40 transition-colors hover:text-danger disabled:opacity-20"
                >
                  <Trash2 className="h-2.5 w-2.5" />
                  Del col
                </button>
                <input
                  value={header}
                  onChange={(e) =>
                    onChange({
                      ...block,
                      headers: replaceAt(block.headers, c, e.target.value),
                    })
                  }
                  onFocus={(e) =>
                    onFieldFocus({
                      element: e.target,
                      getValue: () => header,
                      setValue: (next) =>
                        onChange({
                          ...block,
                          headers: replaceAt(block.headers, c, next),
                        }),
                    })
                  }
                  placeholder={`Column ${c + 1}`}
                  className={cn(fieldClass, 'font-semibold')}
                />
              </div>
            ))}
            <div className="w-5 shrink-0" />
          </div>
          {block.rows.map((row, r) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={r} className="flex items-center gap-1.5">
              {row.map((cell, c) => (
                <input
                  // eslint-disable-next-line react/no-array-index-key
                  key={c}
                  value={cell}
                  onChange={(e) =>
                    onChange({
                      ...block,
                      rows: replaceCellAt(block.rows, r, c, e.target.value),
                    })
                  }
                  onFocus={(e) =>
                    onFieldFocus({
                      element: e.target,
                      getValue: () => cell,
                      setValue: (next) =>
                        onChange({
                          ...block,
                          rows: replaceCellAt(block.rows, r, c, next),
                        }),
                    })
                  }
                  className={fieldClass}
                />
              ))}
              <button
                type="button"
                onClick={() => onChange({ ...block, rows: removeAt(block.rows, r) })}
                aria-label="Remove row"
                className="text-ink/40 transition-colors hover:text-danger"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() =>
                onChange({
                  ...block,
                  headers: [...block.headers, `Column ${block.headers.length + 1}`],
                  rows: block.rows.map((row) => [...row, '']),
                })
              }
              className="flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-hover"
            >
              <Plus className="h-3.5 w-3.5" />
              Column
            </button>
            <button
              type="button"
              onClick={() =>
                onChange({ ...block, rows: [...block.rows, block.headers.map(() => '')] })
              }
              className="flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-hover"
            >
              <Plus className="h-3.5 w-3.5" />
              Row
            </button>
          </div>
        </div>
      )}

      {block.type === 'billingTotals' && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-ink/50">
            Inserts the billing summary (totals) table, placed directly below the Products Table.
            Sourced from pricing variables at send time.
          </p>
          <Checkbox
            label="Show discount breakdown (Sub-Total / Discount / GST / Total)"
            checked={block.showDiscount}
            onChange={(e) => onChange({ ...block, showDiscount: e.target.checked })}
          />
        </div>
      )}

      {block.type === 'bankDetails' && (
        <p className="text-xs text-ink/50">
          Inserts the standard bank details table. Values come from Company Settings (Admin &gt;
          Company Settings).
        </p>
      )}

      {block.type === 'productsTable' && (
        <p className="text-xs text-ink/50">
          Inserts the dynamic products/pricing table built from the lead&apos;s selected products.
          Replaced at send time with the actual table rows - no editable fields here.
        </p>
      )}

      {block.type === 'tcLinks' && (
        <div className="flex flex-col gap-2">
          <Input
            label="Prompt"
            value={block.prompt}
            onChange={(e) => onChange({ ...block, prompt: e.target.value })}
            onFocus={(e) =>
              onFieldFocus({
                element: e.target,
                getValue: () => block.prompt,
                setValue: (next) => onChange({ ...block, prompt: next }),
              })
            }
            placeholder={TC_PROMPT_DEFAULT}
          />
          <p className="text-xs text-ink/50">
            Adds the prompt above, then one blue &quot;Open and Review the Terms &amp;
            Condition&quot; button per document the lead&apos;s products require: API (any{' '}
            <code className="font-mono">API</code> / <code className="font-mono">API_*</code>{' '}
            product), MFTOOLS, or All Products. Any product outside those two families means the All
            Products document only. Buttons are generated at send time - they cannot appear in a
            preview because the acceptance links are minted per lead on the real send.
          </p>
        </div>
      )}

      {block.type === 'additionalArns' && (
        <p className="text-xs text-ink/50">
          Inserts the Additional ARNs table (# / ARN / Client Name / AUM) built from the lead&apos;s
          extra ARNs. Collapses to nothing when the lead has none - no editable fields here.
        </p>
      )}

      {block.type === 'installments' && (
        <p className="text-xs text-ink/50">
          Inserts the installment breakdown table (paid earlier / paid now / due, with the
          paid-so-far and amount-due summary). Collapses to nothing when there is no payment
          schedule - no editable fields here.
        </p>
      )}

      {block.type === 'raw' && (
        <textarea
          ref={rawResize.ref}
          value={block.html}
          onChange={(e) => onChange({ ...block, html: e.target.value })}
          onFocus={(e) =>
            onFieldFocus({
              element: e.target,
              getValue: () => block.html,
              setValue: (next) => onChange({ ...block, html: next }),
            })
          }
          rows={3}
          placeholder="<p>Custom HTML...</p>"
          style={{ overflowAnchor: 'none', minHeight: '5rem' }}
          className={cn(fieldClass, 'font-mono resize-none overflow-hidden')}
        />
      )}
    </div>
  );
};

const AdminEmailTemplateEditorPage = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const templateId = id ? Number(id) : undefined;
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: existing, isLoading: isLoadingTemplate } = useEmailTemplate(templateId ?? 0);
  const { data: variables = [] } = useEmailVariables();
  const createTemplate = useCreateEmailTemplate();
  const updateTemplate = useUpdateEmailTemplate();
  const isSaving = createTemplate.isPending || updateTemplate.isPending;

  const [blocks, setBlocks] = useState<EmailBlock[]>(() => [
    createBlock('heading'),
    createBlock('paragraph'),
  ]);
  const [bodyText, setBodyText] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [pendingDeactivateValues, setPendingDeactivateValues] = useState<MetaFormValues | null>(
    null,
  );
  const wasActiveRef = useRef(true);
  const activeFieldRef = useRef<ActiveField | null>(null);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);
  const [previewHeight, setPreviewHeight] = useState(640);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<MetaFormValues>({
    resolver: zodResolver(metaSchema),
    defaultValues: {
      name: '',
      slug: '',
      category: 'sales',
      department: '',
      referenceTypeCode: '',
      subject: '',
      defaultTo: '',
      defaultCc: '',
      defaultBcc: '',
      replyTo: '',
      isActive: true,
    },
  });

  useEffect(() => {
    if (!existing) return;
    reset({
      name: existing.name,
      slug: existing.slug,
      category: existing.category,
      department: existing.department ?? '',
      referenceTypeCode: existing.referenceTypeCode ?? '',
      subject: existing.subject,
      defaultTo: existing.defaultTo ?? '',
      defaultCc: existing.defaultCc ?? '',
      defaultBcc: existing.defaultBcc ?? '',
      replyTo: existing.replyTo ?? '',
      isActive: existing.isActive,
    });
    wasActiveRef.current = existing.isActive;
    const parsed = parseBlocksFromHtml(existing.bodyHtml);
    setBlocks(parsed ?? [{ id: crypto.randomUUID(), type: 'raw', html: existing.bodyHtml }]);
    setBodyText(existing.bodyText);
  }, [existing, reset]);

  const nameValue = watch('name');
  useEffect(() => {
    if (isEditMode || slugTouched) return;
    setValue('slug', slugify(nameValue ?? ''));
  }, [nameValue, isEditMode, slugTouched, setValue]);

  const groupedVariables = useMemo(() => {
    const groups = new Map<string, EmailVariableDefinition[]>();
    variables.forEach((variable) => {
      const list = groups.get(variable.category) ?? [];
      list.push(variable);
      groups.set(variable.category, list);
    });
    return Array.from(groups.entries());
  }, [variables]);

  const effectiveBodyHtml = useMemo(() => renderBlocksToHtml(blocks), [blocks]);
  const previewDoc = useMemo(
    () => buildPreviewDoc(effectiveBodyHtml, variables),
    [effectiveBodyHtml, variables],
  );

  // Auto-size the preview to its content instead of a fixed height with an
  // internal y-scrollbar. A ResizeObserver (not just the one-shot onLoad
  // measurement) keeps the height accurate as images/fonts finish loading
  // inside the iframe and reflow its content.
  //
  // Measured off the #ak-preview-root card, NOT documentElement. An iframe's
  // <html> is stretched to the iframe's own height by the layout engine, so
  // `documentElement.scrollHeight` is `max(content, current iframe height)` -
  // a one-way ratchet: the iframe grew, <html> grew to fill it, the observer
  // read the larger value and grew the iframe again. It could never shrink, so
  // trailing dead space accumulated below the email card and the preview kept
  // running on past the end of the message. The card measures its real content
  // height and shrinks correctly.
  const previewObserverRef = useRef<ResizeObserver | null>(null);

  const handlePreviewLoad = (): void => {
    const doc = previewIframeRef.current?.contentWindow?.document;
    const root = doc?.getElementById('ak-preview-root');
    if (!root) return;
    previewObserverRef.current?.disconnect();
    const updateHeight = (): void =>
      setPreviewHeight(Math.max(240, Math.ceil(root.getBoundingClientRect().height)));
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(root);
    previewObserverRef.current = observer;
  };

  useEffect(() => () => previewObserverRef.current?.disconnect(), []);

  // Inserts at the field's actual cursor/selection position (not just
  // appended to the end), then restores focus and caret right after the
  // inserted token, matching how the field looked before the click.
  const insertVariable = (key: string): void => {
    const field = activeFieldRef.current;
    if (!field) {
      toast.error('Click into a text field first, then insert the variable.');
      return;
    }
    const { element, setValue: setFieldValue } = field;
    const token = `{{${key}}}`;
    const text = element.value;
    const start = element.selectionStart ?? text.length;
    const end = element.selectionEnd ?? start;
    setFieldValue(`${text.slice(0, start)}${token}${text.slice(end)}`);
    const caret = start + token.length;
    requestAnimationFrame(() => {
      element.focus({ preventScroll: true });
      element.setSelectionRange(caret, caret);
    });
  };

  const makeMetaFocus =
    (fieldName: keyof MetaFormValues) =>
    (e: React.FocusEvent<HTMLInputElement>): void => {
      activeFieldRef.current = {
        element: e.target,
        getValue: () => e.target.value,
        setValue: (next) => setValue(fieldName, next as never),
      };
    };

  const updateBlockAt = (index: number, next: EmailBlock): void => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? next : b)));
  };

  const removeBlockAt = (index: number): void => {
    setBlocks((prev) => prev.filter((_, i) => i !== index));
  };

  const addBlock = (type: EmailBlockType): void => {
    setBlocks((prev) => [...prev, createBlock(type)]);
  };

  // Keyboard/click-operable alternative to drag-reordering (the grip handle
  // only supports mouse/touch drag) - moves a block one position up or down.
  const moveBlockBy = (index: number, delta: number): void => {
    setBlocks((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });
  };

  const handleDragOverIndex = (overIndex: number): void => {
    if (dragIndex === null || dragIndex === overIndex) return;
    setBlocks((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(overIndex, 0, moved);
      return next;
    });
    setDragIndex(overIndex);
  };

  const saveErrorMessage = (err: unknown): string => {
    if (err instanceof AxiosError) {
      const data = err.response?.data as ApiError | undefined;
      const firstFieldError = data?.errors?.[0];
      if (firstFieldError) return `${firstFieldError.field}: ${firstFieldError.message}`;
      if (data?.message) return data.message;
    }
    return 'Could not save the template. Please try again.';
  };

  const submitValues = (values: MetaFormValues): void => {
    const payload = {
      ...values,
      department: values.department || null,
      referenceTypeCode: values.referenceTypeCode || null,
      defaultTo: values.defaultTo || null,
      defaultCc: values.defaultCc || null,
      defaultBcc: values.defaultBcc || null,
      replyTo: values.replyTo || null,
      bodyHtml: effectiveBodyHtml,
      // Plain-text fallback is required server-side; derive it from the
      // rendered blocks if the user left the field blank instead of failing
      // the save over a field most people don't realize they need to fill.
      bodyText: bodyText.trim() || htmlToPlainText(effectiveBodyHtml),
    };

    if (isEditMode && templateId) {
      updateTemplate.mutate(
        { id: templateId, payload },
        {
          onSuccess: () => {
            toast.success('Template updated');
            void navigate(ROUTES.ADMIN_EMAIL_TEMPLATES);
          },
          onError: (err) => toast.error(saveErrorMessage(err)),
        },
      );
      return;
    }

    createTemplate.mutate(payload, {
      onSuccess: () => {
        toast.success('Template created');
        void navigate(ROUTES.ADMIN_EMAIL_TEMPLATES);
      },
      onError: (err) => toast.error(saveErrorMessage(err)),
    });
  };

  // Deactivating a template (saving with "Active" unchecked) removes it from
  // every Sales/Billing/Service composer immediately - confirm before that
  // happens instead of letting an accidental uncheck+save silently do it.
  const onSubmit = (values: MetaFormValues): void => {
    if (isEditMode && wasActiveRef.current && !values.isActive) {
      setPendingDeactivateValues(values);
      return;
    }
    submitValues(values);
  };

  const confirmDeactivateAndSave = (): void => {
    if (!pendingDeactivateValues) return;
    submitValues(pendingDeactivateValues);
    setPendingDeactivateValues(null);
  };

  if (isEditMode && isLoadingTemplate) {
    return <p className="text-sm text-ink/50">Loading template…</p>;
  }

  return (
    <form
      onSubmit={(e) => {
        handleSubmit(onSubmit)(e).catch(() => {});
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link
            to={ROUTES.ADMIN_EMAIL_TEMPLATES}
            className="flex items-center gap-1 text-sm font-semibold text-ink/50 transition-colors hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to templates
          </Link>
          <h1 className="mt-1.5 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            {isEditMode ? 'Edit Template' : 'New Template'}
          </h1>
        </div>
        <Button type="submit" disabled={isSaving} className="flex items-center gap-1.5">
          <Save className="h-4 w-4" />
          {isSaving ? 'Saving…' : 'Save template'}
        </Button>
      </div>

      <Card className="mt-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Name"
            placeholder="Quotation Draft"
            error={errors.name?.message}
            onFocus={makeMetaFocus('name')}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('name')}
          />
          <Input
            label="Slug"
            placeholder="quotation-draft"
            error={errors.slug?.message}
            onFocus={makeMetaFocus('slug')}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('slug', { onChange: () => setSlugTouched(true) })}
          />
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Category"
                options={EMAIL_TEMPLATE_CATEGORY_OPTIONS}
                error={errors.category?.message}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <Input
            label="Department"
            placeholder="Sales RM"
            helperText="Which job role typically uses this template"
            onFocus={makeMetaFocus('department')}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('department')}
          />
          <div className="flex flex-col gap-1.5">
            <Controller
              name="referenceTypeCode"
              control={control}
              render={({ field }) => (
                <SelectField
                  label="Reference type code"
                  options={[
                    { value: '', label: 'None (no reference number)' },
                    { value: 'QT', label: 'QT - Quotation' },
                    { value: 'RN', label: 'RN - Renewal' },
                    { value: 'INV', label: 'INV - Tax Invoice' },
                    { value: 'SV', label: 'SV - Service' },
                    { value: 'PC', label: 'PC - Payment Confirmation' },
                  ]}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
            {watch('referenceTypeCode') && (
              <p className="text-xs text-ink/50 font-mono">
                {`AK/${watch('referenceTypeCode')}/{YYYY}/{MM}/{code}001`}
              </p>
            )}
          </div>
          <div className="sm:col-span-2">
            <Input
              label="Subject"
              placeholder="Quotation - ARN: {{arn}}"
              error={errors.subject?.message}
              onFocus={makeMetaFocus('subject')}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('subject')}
            />
          </div>
          <Input
            label="Default To"
            placeholder="Optional pre-fill"
            onFocus={makeMetaFocus('defaultTo')}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('defaultTo')}
          />
          <Input
            label="Default CC"
            placeholder="Optional pre-fill"
            onFocus={makeMetaFocus('defaultCc')}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('defaultCc')}
          />
          <Input
            label="Default BCC"
            placeholder="Optional pre-fill"
            onFocus={makeMetaFocus('defaultBcc')}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('defaultBcc')}
          />
          <Input
            label="Reply-To"
            placeholder="Optional"
            onFocus={makeMetaFocus('replyTo')}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...register('replyTo')}
          />
          <div className="flex items-center sm:col-span-2">
            <Checkbox
              label="Active (usable when sending)"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('isActive')}
            />
          </div>
        </div>
      </Card>

      <div className="mt-6 flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-wider text-ink/40">Template Body</p>
        <div className="flex items-center gap-1 rounded-lg border border-line bg-white p-1 shadow-sm">
          {VIEW_MODE_OPTIONS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setViewMode(value)}
              aria-pressed={viewMode === value}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                viewMode === value
                  ? 'bg-brand text-white'
                  : 'text-ink/60 hover:bg-cream hover:text-ink',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-4 pb-8 lg:grid-cols-24">
        {viewMode !== 'preview' && (
          <Card
            className="flex max-h-[calc(100vh-9rem)] flex-col self-start lg:sticky lg:top-2 lg:col-span-5"
            padding="sm"
          >
            <p className="px-1 pb-2 text-xs font-bold uppercase tracking-wider text-ink/40">
              Variables
            </p>
            <p className="px-1 pb-3 text-xs text-ink/50">
              Click a field to edit, then click a variable to insert it.
            </p>
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1">
              {groupedVariables.map(([category, items]) => (
                <div key={category}>
                  <p className="pb-1.5 text-[11px] font-bold uppercase tracking-wide text-ink/40">
                    {category}
                  </p>
                  <div className="flex flex-col gap-1">
                    {items.map((variable) => (
                      <button
                        key={variable.key}
                        type="button"
                        onClick={() => insertVariable(variable.key)}
                        title={variable.source}
                        className="rounded-md border border-line bg-cream/40 px-2 py-1 text-left text-xs font-mono text-ink/80 transition-colors hover:border-brand hover:bg-brand-tint hover:text-brand"
                      >
                        {`{{${variable.key}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {viewMode !== 'preview' && (
          <Card
            className={cn(
              viewMode === 'editor' ? 'lg:col-span-19' : 'lg:col-span-10',
              'self-start',
            )}
            padding="sm"
          >
            <p className="px-1 pb-3 text-xs font-bold uppercase tracking-wider text-ink/40">
              Drag blocks to reorder. Fill in the content - styling is fixed.
            </p>
            <div className="flex flex-col gap-3">
              {blocks.map((block, index) => (
                <BlockCard
                  key={block.id}
                  block={block}
                  dragging={dragIndex === index}
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={() => handleDragOverIndex(index)}
                  onDragEnd={() => setDragIndex(null)}
                  onChange={(next) => updateBlockAt(index, next)}
                  onRemove={() => removeBlockAt(index)}
                  onFieldFocus={(field) => {
                    activeFieldRef.current = field;
                  }}
                  onMoveUp={() => moveBlockBy(index, -1)}
                  onMoveDown={() => moveBlockBy(index, 1)}
                  canMoveUp={index > 0}
                  canMoveDown={index < blocks.length - 1}
                />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
              {ADD_BLOCK_TYPES.map((type) => {
                const Icon = BLOCK_TYPE_ICONS[type];
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addBlock(type)}
                    className="flex items-center gap-1.5 rounded-md border border-line bg-white px-2.5 py-1.5 text-xs font-semibold text-ink/70 transition-colors hover:border-brand hover:bg-brand-tint hover:text-brand"
                  >
                    <Plus className="h-3 w-3" />
                    <Icon className="h-3.5 w-3.5" />
                    {BLOCK_TYPE_LABELS[type]}
                  </button>
                );
              })}
            </div>

            <p className="px-1 pb-1 text-xs font-bold uppercase tracking-wider text-ink/40">
              Plain text fallback
            </p>
            <p className="px-1 pb-2 text-xs text-ink/50">
              Optional - auto-generated from the blocks above if left blank.
            </p>
            <Textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={6}
              className="font-mono text-xs"
              placeholder="Dear {{client_name}}, ..."
            />
          </Card>
        )}

        {viewMode !== 'editor' && (
          <Card
            className={cn(
              viewMode === 'preview' ? 'lg:col-span-24' : 'lg:col-span-9',
              'self-start',
            )}
            padding="sm"
          >
            <p className="px-1 pb-2 text-xs font-bold uppercase tracking-wider text-ink/40">
              Preview (sample data)
            </p>
            <iframe
              ref={previewIframeRef}
              title="Email preview"
              srcDoc={previewDoc}
              // allow-same-origin and NOTHING else, so handlePreviewLoad can
              // measure the card's height. NEVER add allow-scripts: the pair lets
              // the frame drop its own sandbox and reach the parent DOM. The body
              // is also run through stripExecutable (buildPreviewDoc), so a raw
              // HTML block cannot execute either way.
              sandbox="allow-same-origin"
              onLoad={handlePreviewLoad}
              style={{ height: previewHeight, overflow: 'hidden' }}
              className="w-full rounded-md border border-line"
            />
          </Card>
        )}
      </div>

      <Dialog
        open={!!pendingDeactivateValues}
        onOpenChange={(open) => {
          if (!open) setPendingDeactivateValues(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate this template?</DialogTitle>
            <DialogDescription>
              This template will no longer be usable to send emails (it stays in the system, it is
              not permanently deleted). Sales, Billing, and Service composers using this template
              will fail to load a preview until it is reactivated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPendingDeactivateValues(null)}
            >
              Cancel
            </Button>
            <Button type="button" variant="danger" onClick={confirmDeactivateAndSave}>
              Deactivate &amp; Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default AdminEmailTemplateEditorPage;
